import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 🎯 CATIA-Style Leader Arrow (Ok İşaretçi)
 *
 * Professional leader line with arrow pointing to a specific location
 * and optional text annotation.
 *
 * Features:
 * - Leader line from point to text
 * - Arrow head pointing to target
 * - Optional text label
 * - Customizable line style (solid/dashed/dotted)
 * - CATIA-style professional rendering
 */

export interface OkIsaretciOptions {
    renk?: string;
    kalinlik?: number;
    tip?: 'solid' | 'dashed' | 'dotted';
    metin?: string;
    metinBoyut?: number;
}

export class FikoOkIsaretci extends THREE.Group {
    override type = 'FikoOkIsaretci';

    // 🔴 SABİT BOYUT - Kamera zoom'una göre scale ayarlanır
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private baslangic: THREE.Vector3;  // Arrow point (target)
    private bitis: THREE.Vector3;      // Text anchor point
    private renk: string;
    private kalinlik: number;
    private tip: 'solid' | 'dashed' | 'dotted';
    private metin: string;
    private metinBoyut: number;

    // Three.js objects
    private leaderLine: THREE.Line | null = null;
    private arrowHead: THREE.Mesh | null = null;
    private textSprite: THREE.Sprite | null = null;

    // Canvas for text
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        baslangic: THREE.Vector3,
        bitis: THREE.Vector3,
        ayarlar?: OkIsaretciOptions
    ) {
        super();

        this.baslangic = baslangic.clone();
        this.bitis = bitis.clone();
        this.renk = ayarlar?.renk || '#00FF00';
        this.kalinlik = ayarlar?.kalinlik || 2;
        this.tip = ayarlar?.tip || 'solid';
        this.metin = ayarlar?.metin || '';
        this.metinBoyut = ayarlar?.metinBoyut || 12;

        // Initialize canvas
        this.canvas = document.createElement('canvas');
        this.canvas.width = 128;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.magFilter = THREE.LinearFilter;

        this.name = 'Ok İşaretçi';
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.textSprite) {
                this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { /* Ignored */ }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.textSprite) return;
        const originalScale = this.textSprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.textSprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.textSprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.textSprite) {
            this.textSprite.userData.originalScale = new THREE.Vector3().copy(this.textSprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createLeaderLine();
        this.createArrowHead();
        if (this.metin) {
            this.createTextSprite();
        }
    }

    private createLeaderLine(): void {
        const geometry = new THREE.BufferGeometry().setFromPoints([this.baslangic, this.bitis]);

        let material: THREE.LineBasicMaterial | THREE.LineDashedMaterial;

        if (this.tip === 'dashed') {
            material = new THREE.LineDashedMaterial({
                color: new THREE.Color(this.renk),
                linewidth: this.kalinlik,
                dashSize: 0.2,
                gapSize: 0.1,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false
            });
            this.leaderLine = new THREE.Line(geometry, material);
            this.leaderLine.computeLineDistances();
        } else if (this.tip === 'dotted') {
            material = new THREE.LineDashedMaterial({
                color: new THREE.Color(this.renk),
                linewidth: this.kalinlik,
                dashSize: 0.05,
                gapSize: 0.1,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false
            });
            this.leaderLine = new THREE.Line(geometry, material);
            this.leaderLine.computeLineDistances();
        } else {
            material = new THREE.LineBasicMaterial({
                color: new THREE.Color(this.renk),
                linewidth: this.kalinlik,
                transparent: true,
                opacity: 0.9,
                depthTest: false,
                depthWrite: false
            });
            this.leaderLine = new THREE.Line(geometry, material);
        }

        this.add(this.leaderLine);
    }

    private createArrowHead(): void {
        const direction = new THREE.Vector3().subVectors(this.bitis, this.baslangic).normalize();

        const arrowGeometry = new THREE.ConeGeometry(0.1, 0.3, 8);
        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(this.renk),
            depthTest: false,
            depthWrite: false
        });

        this.arrowHead = new THREE.Mesh(arrowGeometry, arrowMaterial);
        this.arrowHead.position.copy(this.baslangic);
        this.arrowHead.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.negate());
        this.add(this.arrowHead);
    }

    private createTextSprite(): void {
        this.updateTextTexture();

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false,
            depthWrite: false
        });

        this.textSprite = new THREE.Sprite(spriteMaterial);
        this.textSprite.position.copy(this.bitis);
        this.textSprite.scale.set(2, 1, 1);
        this.add(this.textSprite);
    }

    private updateTextTexture(): void {
        if (!this.metin) return;

        const ctx = this.context;
        const fontSize = this.metinBoyut;
        ctx.font = `bold ${fontSize}px Arial`;

        const metrics = ctx.measureText(this.metin);
        const textWidth = metrics.width;
        const textHeight = fontSize * 1.5;

        const scale = 2;
        const width = Math.pow(2, Math.ceil(Math.log2(textWidth * scale + 20)));
        const height = Math.pow(2, Math.ceil(Math.log2(textHeight * scale)));

        this.canvas.width = width;
        this.canvas.height = height;

        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.clearRect(0, 0, width / scale, height / scale);

        // Background
        ctx.fillStyle = 'rgba(0, 26, 26, 0.9)';
        ctx.fillRect(5, 2, textWidth + 10, textHeight);

        // Text
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.metin, 10, height / scale / 2);

        this.texture.needsUpdate = true;
    }

    setText(text: string): void {
        this.metin = text;
        if (!this.textSprite && text) {
            this.createTextSprite();
        } else if (this.textSprite) {
            this.updateTextTexture();
        }
    }

    setColor(color: string): void {
        this.renk = color;
        if (this.leaderLine) (this.leaderLine.material as THREE.LineBasicMaterial).color.set(color);
        if (this.arrowHead) (this.arrowHead.material as THREE.MeshBasicMaterial).color.set(color);
        this.updateTextTexture();
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.leaderLine) {
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as THREE.Material).dispose();
        }
        if (this.arrowHead) {
            this.arrowHead.geometry.dispose();
            (this.arrowHead.material as THREE.Material).dispose();
        }
        if (this.textSprite) {
            this.textSprite.material.map?.dispose();
            this.textSprite.material.dispose();
        }
        this.texture.dispose();
    }
}
